import type { RootState } from '@/store';
import type { IGuestUser, IPrivacy } from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { QuoteApi } from '../api';
import type { IAuctionHouseInfo, IShippingFormFields } from '../types';
import { buildNewQuote } from '../utils';

interface IQuoteState {
  auctionHouse?: IAuctionHouseInfo;
  creating: boolean;
  hasError: boolean;
  privacy: IPrivacy;
  shippingInfo?: IShippingFormFields;
}

const initialState: IQuoteState = {
  auctionHouse: undefined,
  creating: false,
  hasError: false,
  shippingInfo: undefined,
  privacy: {
    hasConsent1: false,
    hasConsent2: false,
    hasConsent3: false,
    hasConsent4: false,
    hasConsent5: false,
  },
};

export const createQuote = createAsyncThunk('quote/create', async (_, { getState }) => {
  const {
    quote: { auctionHouse, privacy, shippingInfo },
    users: { user },
  } = getState() as RootState;

  const newQuote = buildNewQuote(
    user as IGuestUser,
    auctionHouse as IAuctionHouseInfo,
    shippingInfo as IShippingFormFields,
    privacy
  );

  return QuoteApi.create(newQuote);
});

export const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    updateAuctionHouse: (state, action: PayloadAction<IAuctionHouseInfo>) => {
      state.auctionHouse = action.payload;
    },
    updateShipping: (state, action: PayloadAction<IShippingFormFields>) => {
      state.shippingInfo = action.payload;
    },
    updatePrivacy: (state, action: PayloadAction<IPrivacy>) => {
      state.privacy = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(createQuote.pending, (state) => {
        if (!state.creating) {
          state.creating = true;
          state.hasError = false;
        }
      })
      .addCase(createQuote.fulfilled, (state) => {
        state.creating = false;
        state.auctionHouse = undefined;
        state.shippingInfo = undefined;
        state.privacy = initialState.privacy;
      })
      .addCase(createQuote.rejected, (state) => {
        state.creating = false;
        state.hasError = true;
      });
  },
});

export const { updateAuctionHouse, updatePrivacy, updateShipping } = quoteSlice.actions;

export default quoteSlice.reducer;
